(() => {
  'use strict';

  const cfg = window.PerformerConfig || {};
  const proxyBase = cfg.proxyBase || '/apps/performer';
  const sessionKeyName = cfg.sessionStorageKey || 'performer.session.v1';
  const browserKeyName = cfg.browserStorageKey || 'performer.browser.v1';
  const pendingTokenKeyName = cfg.pendingTokenStorageKey || 'performer.pending.token.v1';
  const handoffStorageKeyName = cfg.handoffStorageKey || 'performer.handoff.v1';
  const handoffHashKeyName = cfg.handoffHashKey || 'pf_handoff';
  const portableHandoffParamName = cfg.portableHandoffParam || 'pf_handoff';
  const fastrrCouponParam = cfg.fastrrCouponParam || 'fastrrCouponCodeFromLink';
  const requestTimeoutMs = Math.max(1000, Number(cfg.requestTimeoutMs || 2500));

  let currentEntitlement = null;
  let currentSessionKey = null;
  let currentCorrelationToken = null;
  let cartSyncPromise = null;
  let fallbackCartClearPromise = null;

  function storageGet(key) { try { return window.localStorage.getItem(key); } catch (_) { return null; } }
  function storageSet(key, value) { try { window.localStorage.setItem(key, value); } catch (_) {} }
  function storageRemove(key) { try { window.localStorage.removeItem(key); } catch (_) {} }
  function sessionGet(key) { try { return window.sessionStorage.getItem(key); } catch (_) { return null; } }
  function sessionRemove(key) { try { window.sessionStorage.removeItem(key); } catch (_) {} }
  function sessionSet(key, value) { try { window.sessionStorage.setItem(key, value); } catch (_) {} }

  function encodeHandoff(value) {
    try {
      return window.btoa(JSON.stringify(value || {})).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    } catch (_) { return ''; }
  }

  function isPortableHandoffToken(value) {
    return /^pfh1\.rcp_[a-zA-Z0-9]+\.\d{10,13}\.[0-9a-fA-F]{64}$/.test(String(value || ''));
  }


  function rootUrl() {
    return window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
  }

  function clearHandoffState() {
    sessionRemove(handoffStorageKeyName);
    sessionRemove(pendingTokenKeyName);
    try {
      const url = new URL(window.location.href);
      let changed = false;

      if (url.searchParams.has(portableHandoffParamName)) {
        url.searchParams.delete(portableHandoffParamName);
        changed = true;
      }

      const rawHash = String(url.hash || '').replace(/^#/, '');
      if (rawHash) {
        const params = new URLSearchParams(rawHash);
        if (params.has(handoffHashKeyName)) {
          params.delete(handoffHashKeyName);
          url.hash = params.toString() ? `#${params.toString()}` : '';
          changed = true;
        }
      }

      if (changed) replaceVisibleUrl(url);
    } catch (_) {}
  }

  function randomId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    const bytes = new Uint8Array(24);
    if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bytes);
    else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }

  function browserId() {
    let value = storageGet(browserKeyName);
    if (!value) { value = randomId(); storageSet(browserKeyName, value); }
    return value;
  }

  async function post(path, body, options = {}) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeout = window.setTimeout(() => { try { controller && controller.abort(); } catch (_) {} }, requestTimeoutMs);
    try {
      const response = await fetch(`${proxyBase}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
        credentials: 'same-origin',
        cache: 'no-store',
        keepalive: Boolean(options.keepalive),
        signal: controller ? controller.signal : undefined
      });
      let data = {};
      try { data = await response.json(); } catch (_) {}
      return { response, data, transportError: false };
    } catch (error) {
      return { response: null, data: { ok: false, error: 'performer_unavailable' }, transportError: true, cause: error };
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function replaceVisibleUrl(url) {
    const search = url.searchParams.toString();
    window.history.replaceState(window.history.state, '', `${url.pathname}${search ? `?${search}` : ''}${url.hash}`);
  }


  function setPortableHandoffToken(token) {
    if (!isPortableHandoffToken(token)) return false;

    const encoded = encodeHandoff({ token: String(token), coupon: '' });
    if (encoded) sessionSet(handoffStorageKeyName, encoded);

    try {
      const url = new URL(window.location.href);
      let changed = false;

      if ((url.searchParams.get(portableHandoffParamName) || '') !== token) {
        url.searchParams.set(portableHandoffParamName, token);
        changed = true;
      }

      const rawHash = String(url.hash || '').replace(/^#/, '');
      if (rawHash) {
        const params = new URLSearchParams(rawHash);
        if (params.has(handoffHashKeyName)) {
          params.delete(handoffHashKeyName);
          url.hash = params.toString() ? `#${params.toString()}` : '';
          changed = true;
        }
      }

      if (changed) replaceVisibleUrl(url);
      return true;
    } catch (_) {
      return false;
    }
  }

  function cleanInvitationUrl(options = {}) {
    try {
      const url = new URL(window.location.href);
      let changed = false;
      if (options.offer !== false && url.searchParams.has(cfg.tokenParam || 'pf_offer')) {
        url.searchParams.delete(cfg.tokenParam || 'pf_offer'); changed = true;
      }
      if (options.coupon === true && url.searchParams.has(fastrrCouponParam)) {
        url.searchParams.delete(fastrrCouponParam); changed = true;
      }
      if (changed) replaceVisibleUrl(url);
    } catch (_) {}
  }

  function ensureValidatedCouponUrl(entitlement) {
    try {
      const expected = entitlement && entitlement.checkout && entitlement.checkout.coupon_code
        ? String(entitlement.checkout.coupon_code)
        : '';
      if (!expected) {
        cleanInvitationUrl({ offer: false, coupon: true });
        return false;
      }
      const url = new URL(window.location.href);
      const current = url.searchParams.get(fastrrCouponParam) || '';
      if (String(current).toUpperCase() === expected.toUpperCase()) return true;
      url.searchParams.set(fastrrCouponParam, expected);
      replaceVisibleUrl(url);
      return true;
    } catch (_) { return false; }
  }

  function shouldForgetSession(error) {
    return [
      'invalid_offer_link','offer_redeemed','offer_revoked','offer_expired','campaign_expired','campaign_not_active',
      'session_expired','session_redeemed','session_revoked','session_not_found','unexpected_shop_domain'
    ].includes(String(error || ''));
  }

  function positiveNumber(value, fallback) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  function renderOfferUi(data) {
    const offer = data && data.offer ? data.offer : {};
    const buyQty = positiveNumber(offer.buy_quantity, 2);
    const getQty = positiveNumber(offer.get_quantity, 2);
    const totalQty = buyQty + getQty;
    const label = `BUY ${buyQty} GET ${getQty} FREE`;
    const lead = `Add any ${totalQty} eligible perfumes • Pay only for ${buyQty}`;

    document.documentElement.dataset.performerOfferBuy = String(buyQty);
    document.documentElement.dataset.performerOfferGet = String(getQty);
    document.documentElement.dataset.performerOfferTotal = String(totalQty);

    document.querySelectorAll('[data-performer-buy-qty]').forEach(el => { el.textContent = String(buyQty); });
    document.querySelectorAll('[data-performer-get-qty]').forEach(el => { el.textContent = String(getQty); });
    document.querySelectorAll('[data-performer-total-qty]').forEach(el => { el.textContent = String(totalQty); });
    document.querySelectorAll('[data-performer-offer-label]').forEach(el => { el.textContent = label; });
    document.querySelectorAll('[data-performer-offer-lead]').forEach(el => { el.textContent = lead; });
  }

  function exposeEntitlement(data) {
    currentEntitlement = data;
    window.PerformerOffer = data;
    const root = document.documentElement;
    root.classList.remove('performer-offer-pending');
    root.classList.add('performer-offer-active');
    root.dataset.performerState = 'active';
    root.dataset.performerOfferType = (data.offer && data.offer.type) || '';
    root.dataset.performerCampaign = (data.campaign && data.campaign.id) || '';
    renderOfferUi(data);
    window.dispatchEvent(new CustomEvent('performer:offerchange', { detail: data }));
  }

  function clearEntitlement() {
    currentEntitlement = null;
    currentSessionKey = null;
    currentCorrelationToken = null;
    window.PerformerOffer = null;
    const root = document.documentElement;
    root.classList.remove('performer-offer-pending', 'performer-offer-active');
    delete root.dataset.performerOfferType;
    delete root.dataset.performerCampaign;
    delete root.dataset.performerOfferBuy;
    delete root.dataset.performerOfferGet;
    delete root.dataset.performerOfferTotal;
    window.dispatchEvent(new CustomEvent('performer:offerchange', { detail: null }));
  }

  async function clearPrivateCartAttributes() {
    if (fallbackCartClearPromise) return fallbackCartClearPromise;
    fallbackCartClearPromise = fetch(`${rootUrl()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { '__performer_session': '', '__performer_campaign': '', '__performer_offer': '' } })
    }).catch(() => null).finally(() => { fallbackCartClearPromise = null; });
    return fallbackCartClearPromise;
  }

  function failOpen(reason, options = {}) {
    const preserveSession = options.preserveSession !== false;
    const hadPrivateState = Boolean(currentEntitlement || currentSessionKey || storageGet(sessionKeyName) || sessionGet(pendingTokenKeyName));
    currentEntitlement = null;
    currentCorrelationToken = null;
    window.PerformerOffer = null;

    const root = document.documentElement;
    root.classList.remove('performer-offer-pending', 'performer-offer-active');
    root.dataset.performerState = 'fallback';
    root.dataset.performerFallbackReason = String(reason || 'unavailable');
    delete root.dataset.performerOfferType;
    delete root.dataset.performerCampaign;
    delete root.dataset.performerOfferBuy;
    delete root.dataset.performerOfferGet;
    delete root.dataset.performerOfferTotal;

    cleanInvitationUrl({ offer: true, coupon: true });
    if (!preserveSession) {
      storageRemove(sessionKeyName);
      currentSessionKey = null;
      clearHandoffState();
    }
    if (hadPrivateState) clearPrivateCartAttributes();

    window.dispatchEvent(new CustomEvent('performer:offerchange', { detail: null }));
    window.dispatchEvent(new CustomEvent('performer:fallback', { detail: { reason: String(reason || 'unavailable') } }));
  }

  async function syncCartAttributes() {
    if (!currentSessionKey || !currentEntitlement) return;
    if (cartSyncPromise) return cartSyncPromise;
    cartSyncPromise = fetch(`${rootUrl()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: {
        '__performer_session': currentCorrelationToken || '',
        '__performer_campaign': currentEntitlement.campaign && currentEntitlement.campaign.id || '',
        '__performer_offer': currentEntitlement.offer && currentEntitlement.offer.type || ''
      } })
    }).catch(() => null).finally(() => { cartSyncPromise = null; });
    return cartSyncPromise;
  }

  async function getCartToken() {
    try {
      const response = await fetch(`${rootUrl()}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
      const cart = await response.json();
      return cart && cart.token ? String(cart.token) : null;
    } catch (_) { return null; }
  }

  async function reportCheckoutStart() {
    if (!currentSessionKey || !currentEntitlement) return;
    await syncCartAttributes();
    const cartToken = await getCartToken();
    post('/checkout-start', { session_key: currentSessionKey, cart_token: cartToken }, { keepalive: true }).catch(() => {});
  }

  async function activateFromToken(token) {
    const result = await post('/claim', { token, browser_id: browserId() });
    const response = result.response;
    const data = result.data || {};

    if (!response || !response.ok || !data.ok) {
      const terminal = response && response.status >= 400 && response.status < 500 && shouldForgetSession(data.error);
      failOpen(data.error || 'claim_failed', { preserveSession: !terminal });
      return { ok: false, status: response ? response.status : 0, terminal };
    }

    currentSessionKey = data.session_key;
    currentCorrelationToken = data.correlation_token || null;
    storageSet(sessionKeyName, currentSessionKey);
    sessionRemove(pendingTokenKeyName);
    setPortableHandoffToken(data.handoff_token || '');
    exposeEntitlement(data);
    cleanInvitationUrl({ offer: true, coupon: false });
    await syncCartAttributes();
    ensureValidatedCouponUrl(data);
    return { ok: true, status: response.status };
  }

  async function resumeSession(sessionKey) {
    const result = await post('/session', { session_key: sessionKey });
    const response = result.response;
    const data = result.data || {};

    if (!response || !response.ok || !data.ok) {
      const terminal = response && response.status >= 400 && response.status < 500 && shouldForgetSession(data.error);
      failOpen(data.error || 'session_resume_failed', { preserveSession: !terminal });
      return false;
    }

    currentSessionKey = sessionKey;
    currentCorrelationToken = data.correlation_token || null;
    setPortableHandoffToken(data.handoff_token || '');
    exposeEntitlement(data);
    await syncCartAttributes();
    ensureValidatedCouponUrl(data);
    return true;
  }

  async function init() {
    const url = new URL(window.location.href);
    const token = url.searchParams.get(cfg.tokenParam || 'pf_offer') || sessionGet(pendingTokenKeyName);
    if (token) {
      await activateFromToken(token);
      return;
    }

    const existing = storageGet(sessionKeyName);
    if (existing) {
      currentSessionKey = existing;
      await resumeSession(existing);
      return;
    }

    document.documentElement.classList.remove('performer-offer-pending');
    document.documentElement.dataset.performerState = 'public';
  }

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !currentEntitlement) return;
    const checkoutTrigger = target.closest('.sr-headless-checkout, .shiprocket-headless button, button[name="checkout"], #CartDrawer-Checkout, [data-ps-native-checkout]');
    if (!checkoutTrigger) return;
    ensureValidatedCouponUrl(currentEntitlement);
    reportCheckoutStart();
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (form && currentEntitlement && (form.id === 'cart' || form.id === 'CartDrawer-Form')) {
      ensureValidatedCouponUrl(currentEntitlement);
      reportCheckoutStart();
    }
  }, true);

  window.Performer = Object.freeze({
    getOffer: () => currentEntitlement,
    getSessionKey: () => currentSessionKey,
    isActive: () => Boolean(currentEntitlement),
    syncCart: syncCartAttributes,
    reportCheckoutStart,
    failOpen: reason => failOpen(reason || 'manual_fallback', { preserveSession: true })
  });

  init().catch(() => failOpen('bridge_exception', { preserveSession: true }));
})();
